import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Save, Loader2, Globe, Layout, Type, BarChart3, CreditCard, Megaphone, Plus, Trash2, ExternalLink, Palette } from 'lucide-react';

export default function AdminLandingCMS() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.getLanding().then(r => { setContent(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const update = (path, value) => {
    setContent(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = c;
      for (let i = 0; i < parts.length - 1; i++) {
        if (obj[parts[i]] === undefined) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return c;
    });
  };

  const updateArrayItem = (arrayPath, index, field, value) => {
    setContent(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const parts = arrayPath.split('.');
      let arr = c;
      for (const p of parts) arr = arr[p];
      arr[index][field] = value;
      return c;
    });
  };

  const removeArrayItem = (arrayPath, index) => {
    setContent(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const parts = arrayPath.split('.');
      let arr = c;
      for (const p of parts) arr = arr[p];
      arr.splice(index, 1);
      return c;
    });
  };

  const addArrayItem = (arrayPath, item) => {
    setContent(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const parts = arrayPath.split('.');
      let arr = c;
      for (const p of parts) {
        if (arr[p] === undefined) arr[p] = [];
        arr = arr[p];
      }
      arr.push(item);
      return c;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateLanding({ content });
      toast.success('Landing page content saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading || !content) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const hero = content.hero || {};
  const stats = content.stats || [];
  const features = content.features || [];
  const plans = content.plans || [];
  const svcSection = content.services_section || {};
  const ctaSection = content.cta_section || {};
  const footer = content.footer || {};
  const pricingSection = content.pricing_section || {};

  return (
    <div data-testid="admin-landing-cms" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Landing Page CMS</h2>
          <p className="text-muted-foreground mt-1">Edit the public landing page content</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <a href="/" target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3 h-3 mr-1.5" />Preview</a>
          </Button>
          <Button data-testid="save-landing" onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save All Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="branding">
        <TabsList className="bg-secondary/50 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="branding"><Palette className="w-3 h-3 mr-1" />Branding</TabsTrigger>
          <TabsTrigger value="hero"><Layout className="w-3 h-3 mr-1" />Hero</TabsTrigger>
          <TabsTrigger value="stats"><BarChart3 className="w-3 h-3 mr-1" />Stats</TabsTrigger>
          <TabsTrigger value="services"><Globe className="w-3 h-3 mr-1" />Services</TabsTrigger>
          <TabsTrigger value="features"><Type className="w-3 h-3 mr-1" />Features</TabsTrigger>
          {/* Plans tab hidden — wallet-based billing */}
          <TabsTrigger value="cta"><Megaphone className="w-3 h-3 mr-1" />CTA & Footer</TabsTrigger>
        </TabsList>

        {/* ── Branding ── */}
        <TabsContent value="branding" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Brand Identity</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Field label="Brand Name" value={content.branding?.brand_name} onChange={v => update('branding.brand_name', v)} />
              <Field label="Page Title (browser tab)" value={content.branding?.page_title} onChange={v => update('branding.page_title', v)} />
              <div className="space-y-1">
                <Label className="text-xs">Logo URL</Label>
                <Input value={content.branding?.logo_url || ''} onChange={e => update('branding.logo_url', e.target.value)} className="h-8 text-sm font-mono" placeholder="/tensornex-logo.png or https://..." />
                {content.branding?.logo_url && (
                  <div className="flex items-center gap-3 mt-2 p-3 rounded-lg bg-secondary/20 border border-border/20">
                    <img src={content.branding.logo_url} alt="Logo preview" className="w-10 h-10 object-contain" />
                    <img src={content.branding.logo_url} alt="Favicon preview" className="w-5 h-5 object-contain" />
                    <span className="text-[10px] text-muted-foreground">Logo + Favicon preview</span>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">Use a transparent PNG. Upload to /public/ or use an external URL.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Favicon URL</Label>
                <Input value={content.branding?.favicon_url || ''} onChange={e => update('branding.favicon_url', e.target.value)} className="h-8 text-sm font-mono" placeholder="/tensornex-logo.png" />
                <p className="text-[10px] text-muted-foreground">Shown in the browser tab. Same image as logo works fine — browsers auto-resize.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Header Background Color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={content.branding?.header_bg_color || '#d9d9d9'} onChange={e => update('branding.header_bg_color', e.target.value)} className="w-10 h-8 rounded border border-border cursor-pointer" />
                  <Input value={content.branding?.header_bg_color || ''} onChange={e => update('branding.header_bg_color', e.target.value)} className="h-8 text-xs font-mono flex-1" placeholder="#d9d9d9 or leave empty for white" />
                </div>
                <p className="text-[10px] text-muted-foreground">Navigation bar background color. Leave empty for default white with blur.</p>
              </div>
            </CardContent>
          </Card>

          {/* Theme Customization */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Theme Customization</CardTitle>
                <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => {
                  update('branding.theme', { primary_color: '#6366f1', accent_color: '#7c3aed', button_radius: 'rounded', button_style: 'gradient' });
                  toast.success('Theme reset to defaults');
                }}>Reset to Default</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={content.branding?.theme?.primary_color || '#6366f1'} onChange={e => update('branding.theme.primary_color', e.target.value)} className="w-10 h-8 rounded border border-border cursor-pointer" />
                    <Input value={content.branding?.theme?.primary_color || '#6366f1'} onChange={e => update('branding.theme.primary_color', e.target.value)} className="h-8 text-xs font-mono flex-1" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">Main brand color. Used for buttons, links, badges, active states.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Accent Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={content.branding?.theme?.accent_color || '#7c3aed'} onChange={e => update('branding.theme.accent_color', e.target.value)} className="w-10 h-8 rounded border border-border cursor-pointer" />
                    <Input value={content.branding?.theme?.accent_color || '#7c3aed'} onChange={e => update('branding.theme.accent_color', e.target.value)} className="h-8 text-xs font-mono flex-1" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">Gradient end color. Used with primary for gradient buttons and banners.</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Button Radius</Label>
                <div className="flex gap-2">
                  {[
                    { id: 'pill', label: 'Pill', preview: 'rounded-full' },
                    { id: 'rounded', label: 'Rounded', preview: 'rounded-xl' },
                    { id: 'subtle', label: 'Subtle', preview: 'rounded-md' },
                    { id: 'sharp', label: 'Sharp', preview: 'rounded-none' },
                  ].map(opt => (
                    <button key={opt.id} onClick={() => update('branding.theme.button_radius', opt.id)}
                      className={`flex-1 py-2 px-3 border text-xs font-medium transition-all ${opt.preview} ${
                        (content.branding?.theme?.button_radius || 'rounded') === opt.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-border/80'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Live Preview */}
              <div className="space-y-2">
                <Label className="text-xs">Live Preview</Label>
                <div className="p-4 rounded-lg bg-secondary/20 border border-border/20 flex flex-wrap items-center gap-3">
                  <button className="px-5 py-2 text-sm font-semibold text-white" style={{
                    background: `linear-gradient(to right, ${content.branding?.theme?.primary_color || '#6366f1'}, ${content.branding?.theme?.accent_color || '#7c3aed'})`,
                    borderRadius: { pill: '9999px', rounded: '12px', subtle: '6px', sharp: '0px' }[content.branding?.theme?.button_radius || 'rounded'],
                    boxShadow: `0 4px 14px ${content.branding?.theme?.primary_color || '#6366f1'}4D`
                  }}>Gradient Button</button>
                  <button className="px-5 py-2 text-sm font-semibold text-white" style={{
                    background: content.branding?.theme?.primary_color || '#6366f1',
                    borderRadius: { pill: '9999px', rounded: '12px', subtle: '6px', sharp: '0px' }[content.branding?.theme?.button_radius || 'rounded']
                  }}>Solid Button</button>
                  <button className="px-5 py-2 text-sm font-semibold" style={{
                    border: `1px solid ${content.branding?.theme?.primary_color || '#6366f1'}`,
                    color: content.branding?.theme?.primary_color || '#6366f1',
                    background: 'transparent',
                    borderRadius: { pill: '9999px', rounded: '12px', subtle: '6px', sharp: '0px' }[content.branding?.theme?.button_radius || 'rounded']
                  }}>Outline Button</button>
                  <div className="w-full h-12 rounded-lg" style={{background: `linear-gradient(135deg, ${content.branding?.theme?.primary_color || '#6366f1'}, ${content.branding?.theme?.accent_color || '#7c3aed'})`}}>
                    <span className="text-white text-xs font-medium p-3">Gradient Banner Preview</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Hero ── */}
        <TabsContent value="hero" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Hero Section</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Badge Text" value={hero.badge} onChange={v => update('hero.badge', v)} />
              <Field label="Title" value={hero.title} onChange={v => update('hero.title', v)} />
              <div className="space-y-1">
                <Label className="text-xs">Subtitle</Label>
                <textarea className="w-full bg-background border border-input rounded-md p-3 text-sm resize-none h-20" value={hero.subtitle || ''} onChange={e => update('hero.subtitle', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CTA Button Text" value={hero.cta_text} onChange={v => update('hero.cta_text', v)} />
                <Field label="Secondary CTA Text" value={hero.cta_secondary_text} onChange={v => update('hero.cta_secondary_text', v)} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Stats ── */}
        <TabsContent value="stats" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Stats Bar</CardTitle>
                <Button size="sm" variant="outline" onClick={() => addArrayItem('stats', { value: '0', label: 'New Stat' })}>
                  <Plus className="w-3 h-3 mr-1" />Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {stats.map((stat, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="flex-1"><Field label={`Stat ${i + 1} Value`} value={stat.value} onChange={v => updateArrayItem('stats', i, 'value', v)} /></div>
                  <div className="flex-1"><Field label="Label" value={stat.label} onChange={v => updateArrayItem('stats', i, 'label', v)} /></div>
                  <Button size="sm" variant="ghost" className="text-destructive mb-0.5" onClick={() => removeArrayItem('stats', i)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Services ── */}
        <TabsContent value="services" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Services Section</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Section Title" value={svcSection.title} onChange={v => update('services_section.title', v)} />
              <div className="space-y-1">
                <Label className="text-xs">Subtitle</Label>
                <textarea className="w-full bg-background border border-input rounded-md p-3 text-sm resize-none h-16" value={svcSection.subtitle || ''} onChange={e => update('services_section.subtitle', e.target.value)} />
              </div>
            </CardContent>
          </Card>
          {(svcSection.highlights || []).map((hl, i) => (
            <Card key={i} className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Highlight #{i + 1}</CardTitle>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeArrayItem('services_section.highlights', i)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Category" value={hl.category} onChange={v => updateArrayItem('services_section.highlights', i, 'category', v)} />
                  <Field label="Title" value={hl.title} onChange={v => updateArrayItem('services_section.highlights', i, 'title', v)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <textarea className="w-full bg-background border border-input rounded-md p-3 text-sm resize-none h-16" value={hl.description || ''} onChange={e => updateArrayItem('services_section.highlights', i, 'description', e.target.value)} />
                </div>
                <Field label="Services (comma-separated IDs)" value={(hl.services || []).join(', ')} onChange={v => updateArrayItem('services_section.highlights', i, 'services', v.split(',').map(s => s.trim()).filter(Boolean))} />
              </CardContent>
            </Card>
          ))}
          <Button size="sm" variant="outline" onClick={() => addArrayItem('services_section.highlights', { category: 'New', title: 'New Highlight', description: '', services: [] })}>
            <Plus className="w-3 h-3 mr-1" />Add Highlight
          </Button>
        </TabsContent>

        {/* ── Features ── */}
        <TabsContent value="features" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground">Feature Cards</h3>
            <Button size="sm" variant="outline" onClick={() => addArrayItem('features', { icon: 'zap', title: 'New Feature', description: '' })}>
              <Plus className="w-3 h-3 mr-1" />Add Feature
            </Button>
          </div>
          {features.map((feat, i) => (
            <Card key={i} className="bg-card border-border/50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-mono">Feature #{i + 1}</span>
                  <Button size="sm" variant="ghost" className="text-destructive h-6" onClick={() => removeArrayItem('features', i)}><Trash2 className="w-3 h-3" /></Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Icon (zap, shield, code, etc.)" value={feat.icon} onChange={v => updateArrayItem('features', i, 'icon', v)} />
                  <div className="col-span-2"><Field label="Title" value={feat.title} onChange={v => updateArrayItem('features', i, 'title', v)} /></div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <textarea className="w-full bg-background border border-input rounded-md p-3 text-sm resize-none h-16" value={feat.description || ''} onChange={e => updateArrayItem('features', i, 'description', e.target.value)} />
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ── Pricing Plans ── */}
        <TabsContent value="pricing" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Pricing Section Header</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Title" value={pricingSection.title} onChange={v => update('pricing_section.title', v)} />
              <Field label="Subtitle" value={pricingSection.subtitle} onChange={v => update('pricing_section.subtitle', v)} />
            </CardContent>
          </Card>
          {plans.map((plan, i) => (
            <Card key={i} className="bg-card border-border/50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium capitalize">{plan.name || `Plan ${i + 1}`}</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs text-muted-foreground">Highlighted</Label>
                      <Switch checked={plan.highlighted || false} onCheckedChange={v => updateArrayItem('plans', i, 'highlighted', v)} />
                    </div>
                    <Button size="sm" variant="ghost" className="text-destructive h-6" onClick={() => removeArrayItem('plans', i)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <Field label="ID" value={plan.id} onChange={v => updateArrayItem('plans', i, 'id', v)} />
                  <Field label="Name" value={plan.name} onChange={v => updateArrayItem('plans', i, 'name', v)} />
                  <Field label="Price" value={plan.price} onChange={v => updateArrayItem('plans', i, 'price', v)} />
                  <Field label="Period" value={plan.period} onChange={v => updateArrayItem('plans', i, 'period', v)} />
                </div>
                <Field label="Description" value={plan.description} onChange={v => updateArrayItem('plans', i, 'description', v)} />
                <Field label="CTA Button Text" value={plan.cta} onChange={v => updateArrayItem('plans', i, 'cta', v)} />
                <div className="space-y-1">
                  <Label className="text-xs">Features (one per line)</Label>
                  <textarea className="w-full bg-background border border-input rounded-md p-3 text-sm resize-none h-24 font-mono" value={(plan.features || []).join('\n')} onChange={e => updateArrayItem('plans', i, 'features', e.target.value.split('\n'))} />
                </div>
              </CardContent>
            </Card>
          ))}
          <Button size="sm" variant="outline" onClick={() => addArrayItem('plans', { id: 'new', name: 'New Plan', price: '$0', period: '/month', description: '', features: [], cta: 'Select', highlighted: false })}>
            <Plus className="w-3 h-3 mr-1" />Add Plan
          </Button>
        </TabsContent>

        {/* ── CTA & Footer ── */}
        <TabsContent value="cta" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">CTA Section</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Title" value={ctaSection.title} onChange={v => update('cta_section.title', v)} />
              <div className="space-y-1">
                <Label className="text-xs">Subtitle</Label>
                <textarea className="w-full bg-background border border-input rounded-md p-3 text-sm resize-none h-16" value={ctaSection.subtitle || ''} onChange={e => update('cta_section.subtitle', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Button Text" value={ctaSection.button_text} onChange={v => update('cta_section.button_text', v)} />
                <Field label="Secondary Text" value={ctaSection.secondary_text} onChange={v => update('cta_section.secondary_text', v)} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Footer</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company Name" value={footer.company_name} onChange={v => update('footer.company_name', v)} />
                <Field label="Tagline" value={footer.tagline} onChange={v => update('footer.tagline', v)} />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <Label className="text-xs">Footer Links</Label>
                <Button size="sm" variant="outline" onClick={() => addArrayItem('footer.links', { label: 'New Link', url: '#' })}><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
              {(footer.links || []).map((link, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="flex-1"><Field label="Label" value={link.label} onChange={v => updateArrayItem('footer.links', i, 'label', v)} /></div>
                  <div className="flex-1"><Field label="URL" value={link.url} onChange={v => updateArrayItem('footer.links', i, 'url', v)} /></div>
                  <Button size="sm" variant="ghost" className="text-destructive mb-0.5" onClick={() => removeArrayItem('footer.links', i)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={value || ''} onChange={e => onChange(e.target.value)} className="h-8 text-sm" />
    </div>
  );
}
